import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/types';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          <div className="lg:col-span-3 relative overflow-hidden">
            <div className="w-full h-52 lg:h-full min-h-[200px]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
          <div className="lg:col-span-2 p-5 md:p-6 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                {article.category}
              </span>
              {article.isHot && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  热门
                </span>
              )}
            </div>
            <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount >= 10000
                  ? `${(article.readCount / 10000).toFixed(1)}万`
                  : article.readCount}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line"></i>
                {article.commentCount}
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200/40 last:border-b-0"
      >
        <div className="w-20 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line"></i>
              {article.readCount >= 10000
                ? `${(article.readCount / 10000).toFixed(1)}万`
                : article.readCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 h-full"
    >
      <div className="relative overflow-hidden">
        <div className="w-full h-44">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
            热门
          </span>
        )}
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-medium bg-background-50/90 text-foreground-700">
          {article.category}
        </span>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-1">
            <div className="w-5 h-5 rounded-full overflow-hidden bg-background-200">
              <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
            </div>
            <span>{article.author}</span>
          </div>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}